/**
 * Uski Markdown za tekstove koje piše trener.
 *
 * Zašto vlastiti parser, a ne biblioteka:
 *
 * 1. Sadržaj piše trener kroz admin i sprema se u bazu. Kad bi se pretvarao u
 *    HTML pa ubacivao kroz `dangerouslySetInnerHTML`, jedan nesretan `<script>`
 *    u tijelu članka bio bi XSS na domeni portala. Ovdje izlaz nije HTML nego
 *    stablo blokova — klijent od njega gradi vlastite elemente, pa nema načina
 *    da tekst iz baze postane markup.
 * 2. Portal i mobilna aplikacija prikazuju ISTI članak. Dva parsera bi se
 *    razišla čim netko doda jednu oznaku, pa bi trenerov tekst izgledao
 *    drukčije u aplikaciji nego u portalu.
 * 3. Podržan je točno onaj podskup koji tekstovi stvarno koriste. Sve što nije
 *    na popisu ostaje običan tekst — nema tihog gubljenja sadržaja.
 *
 * Podržano: `##`/`###` naslovi, odlomci, `-` popis, `1.` numerirani popis,
 * `>` citat, `---` crta, tablica s zaglavljem, te unutar retka
 * `**podebljano**`, `*ukošeno*`, `` `kod` `` i `[tekst](url)`.
 *
 * Namjerno NIJE podržano: sirovi HTML, slike (naslovna slika je zasebno polje),
 * ugniježđeni popisi. Kad zatreba, dodaje se ovdje i dobiva test.
 */

export interface TextSpan {
  kind: 'text';
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
}

export interface LinkSpan {
  kind: 'link';
  text: string;
  href: string;
}

export type Span = TextSpan | LinkSpan;

export type Block =
  | { kind: 'heading'; level: 2 | 3; spans: Span[] }
  | { kind: 'paragraph'; spans: Span[] }
  | { kind: 'list'; ordered: boolean; items: Span[][] }
  | { kind: 'quote'; spans: Span[] }
  /*
    Tablica nosi zaglavlje odvojeno od redaka. Prikaz je na klijentu: portal ima
    širine za prave stupce, telefon nema, pa ondje redak ide složen ispod
    naslova stupca. Bez ovog bloka je trenerova tablica ispadala kao odlomak pun
    uspravnih crta — točno kako je izgledala u aplikaciji.
  */
  | { kind: 'table'; header: Span[][]; rows: Span[][][] }
  | { kind: 'rule' };

/**
 * Samo `http(s)` i `mailto`. `javascript:` i `data:` u `href`-u su izvršni kod
 * na klik — trenerov tekst nema razloga nositi nijedno, pa takva poveznica
 * ostaje običan tekst umjesto da postane klik.
 */
function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (/^https?:\/\//i.test(href)) return href;
  if (/^mailto:[^\s]+@[^\s]+$/i.test(href)) return href;
  return null;
}

const INLINE = /(\[[^\]\n]*\]\([^)\s]+\))|(\*\*[^*\n]+\*\*)|(`[^`\n]+`)|(\*[^*\n]+\*)/;

/** Rekurzija ide po prvom pogotku slijeva — ugniježđene oznake se ne miješaju. */
export function parseInline(src: string): Span[] {
  const out: Span[] = [];
  let rest = src;

  while (rest.length > 0) {
    const m = INLINE.exec(rest);
    if (!m || m.index === undefined) break;

    if (m.index > 0) out.push({ kind: 'text', text: rest.slice(0, m.index) });
    const token = m[0];

    if (m[1]) {
      const link = /^\[([^\]\n]*)\]\(([^)\s]+)\)$/.exec(token);
      const href = link ? safeHref(link[2]!) : null;
      if (link && href) out.push({ kind: 'link', text: link[1] || href, href });
      else out.push({ kind: 'text', text: token });
    } else if (m[2]) {
      out.push({ kind: 'text', text: token.slice(2, -2), bold: true });
    } else if (m[3]) {
      out.push({ kind: 'text', text: token.slice(1, -1), code: true });
    } else {
      out.push({ kind: 'text', text: token.slice(1, -1), italic: true });
    }

    rest = rest.slice(m.index + token.length);
  }

  if (rest.length > 0) out.push({ kind: 'text', text: rest });
  return out.length > 0 ? out : [{ kind: 'text', text: '' }];
}

const HEADING = /^(#{2,3})\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;
const ORDERED = /^\d+[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^(-{3,}|\*{3,}|_{3,})$/;
/** Redak tablice: počinje i završava uspravnom crtom. */
const TABLE_ROW = /^\|(.+)\|$/;
/** Redak razdvajanja ispod zaglavlja: `|---|:--:|` i slično. */
const TABLE_DIVIDER = /^\|[\s:|-]+\|$/;

function tableCells(line: string): string[] {
  return (TABLE_ROW.exec(line)?.[1] ?? '').split('|').map((cell) => cell.trim());
}

/**
 * Tekst u blokove.
 *
 * Prazan red zatvara odlomak; unutar odlomka jedan prijelom je samo prijelom
 * (trener ne piše `<br>`). Popis se prekida praznim redom ili retkom koji nije
 * stavka — bez toga bi jedan zalutali redak progutao ostatak članka.
 */
export function parseMarkdown(src: string | null | undefined): Block[] {
  if (!src) return [];

  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];

  let para: string[] = [];
  let quote: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let table: string[] = [];

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push({ kind: 'paragraph', spans: parseInline(para.join(' ').trim()) });
    para = [];
  };
  const flushQuote = () => {
    if (quote.length === 0) return;
    blocks.push({ kind: 'quote', spans: parseInline(quote.join(' ').trim()) });
    quote = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push({
      kind: 'list',
      ordered: list.ordered,
      items: list.items.map((i) => parseInline(i)),
    });
    list = null;
  };
  /*
    Tablica bez retka razdvajanja nije tablica nego tekst s crtama — takav se
    vraća kao odlomci umjesto da se tiho izgubi.
  */
  const flushTable = () => {
    if (table.length === 0) return;
    const redovi = table;
    table = [];

    if (redovi.length < 2 || !TABLE_DIVIDER.test(redovi[1]!)) {
      for (const line of redovi) blocks.push({ kind: 'paragraph', spans: parseInline(line) });
      return;
    }

    const header = tableCells(redovi[0]!);
    blocks.push({
      kind: 'table',
      header: header.map((cell) => parseInline(cell)),
      // Redak kraći od zaglavlja dobiva prazne ćelije — prikaz tada ne mora pogađati.
      rows: redovi.slice(2).map((line) => {
        const cells = tableCells(line);
        return header.map((_, i) => parseInline(cells[i] ?? ''));
      }),
    });
  };

  const flushAll = () => {
    flushPara();
    flushQuote();
    flushList();
    flushTable();
  };

  for (const raw of lines) {
    const line = raw.trim();

    // Svaki redak koji nije dio tablice zatvara tablicu koja je u tijeku.
    if (table.length > 0 && !TABLE_ROW.test(line)) flushTable();

    if (line === '') {
      flushAll();
      continue;
    }

    if (RULE.test(line)) {
      flushAll();
      blocks.push({ kind: 'rule' });
      continue;
    }

    if (TABLE_ROW.test(line)) {
      flushPara();
      flushQuote();
      flushList();
      table.push(line);
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushAll();
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length === 2 ? 2 : 3,
        spans: parseInline(heading[2]!.trim()),
      });
      continue;
    }

    const quoted = QUOTE.exec(line);
    if (quoted) {
      flushPara();
      flushList();
      quote.push(quoted[1]!);
      continue;
    }

    const bullet = BULLET.exec(line);
    const ordered = ORDERED.exec(line);
    if (bullet || ordered) {
      flushPara();
      flushQuote();
      const isOrdered = Boolean(ordered);
      // Promjena vrste popisa zatvara prethodni — inače bi se pomiješali.
      if (list && list.ordered !== isOrdered) flushList();
      if (!list) list = { ordered: isOrdered, items: [] };
      list.items.push((bullet?.[1] ?? ordered?.[1] ?? '').trim());
      continue;
    }

    flushQuote();
    flushList();
    para.push(line);
  }

  flushAll();
  return blocks;
}

/** Prvi odlomak članka — ide krupnije, kao uvod. Naslov se preskače. */
export function leadIndex(blocks: readonly Block[]): number {
  return blocks.findIndex((b) => b.kind === 'paragraph');
}

export function spansToText(spans: readonly Span[]): string {
  return spans.map((s) => s.text).join('');
}

/** Gruba procjena vremena čitanja; 200 riječi u minuti, minimalno 1. */
export function readingMinutes(src: string | null | undefined): number {
  if (!src) return 0;
  const words = src.trim().split(/\s+/).filter(Boolean).length;
  return words === 0 ? 0 : Math.max(1, Math.round(words / 200));
}
